
/**
 * Pre-Signup Value Proposition Component
 *
 * Landing page showing benefits of becoming a guide
 * Content and layout extracted from GuideSignupFlow.tsx mockup
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/components/guides/signup/PreSignupValue.jsx
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar as _Calendar, Check, Shield, Star as _Star, Mountain as _Mountain, Heart, AlertCircle as _AlertCircle, ChevronRight, Clock } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * PreSignupValue Component
 *
 * @param {Object} props
 * @param {Function} props.onGetStarted - Handler when "Get Started" is clicked
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
const PreSignupValue = ({ onGetStarted, className = '' }) => {
  const [stats, setStats] = useState({
    activeGuideCount: 0,
    loading: true
  });

  // Fetch platform statistics on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/platform/stats`);
        const data = await response.json();

        if (data.success && data.data) {
          setStats({
            activeGuideCount: data.data.totalGuides || 0,
            loading: false
          });
        }
      } catch (error) {
        console.error('Failed to fetch platform stats:', error);
        setStats({ activeGuideCount: 0, loading: false });
      }
    };

    fetchStats();
  }, []);

  const { activeGuideCount } = stats;

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 pb-32 md:pb-8 ${className}`}>
      {/* Hero Section with CTA Above the Fold */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-6">
          <_Mountain className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {activeGuideCount < 50
            ? 'Join the Founding 50 Guides'
            : `Join ${activeGuideCount} Professional Guides Earning $110K/Year`
          }
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
          The outdoor guide marketplace built by guides, for guides. Keep 100% of your booking fees—customers pay the platform fee.
        </p>

        {/* Time Expectation - Above CTA */}
        <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-sm border border-gray-200 mb-8">
          <Clock className="w-5 h-5 text-emerald-600 mr-2" />
          <span className="text-sm font-medium text-gray-700">
            <strong>15-20 minutes</strong> to create your profile • <strong>Complete anytime</strong> • Auto-saves
          </span>
        </div>

        {/* Primary CTA - Above the fold */}
        <div className="mb-4">
          <button
            onClick={onGetStarted}
            className="inline-flex items-center px-8 py-4 bg-emerald-600 text-white text-lg font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
          >
            Create Your Guide Profile
            <ChevronRight className="ml-2 w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-8">
          No credit card required • Free to join • Cancel anytime
        </p>

        {/* Scroll indicator */}
        <p className="text-sm text-gray-500 italic">
          ↓ Or scroll to learn more about what makes us different
        </p>
      </div>

      {/* Value Propositions */}
      <div className="grid md:grid-cols-3 gap-6 mb-12 mt-16">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-lg mb-4">
            <DollarSign className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">You Keep 100% of Your Fees</h3>
          <p className="text-gray-600 text-sm">
            Customers pay an 8% platform fee. You pay nothing and keep every dollar you charge.
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-lg mb-4">
            <Clock className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Professional Infrastructure</h3>
          <p className="text-gray-600 text-sm">
            Payment processing, booking management, customer discovery—worth $50-100K if you built it yourself.
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-lg mb-4">
            <Shield className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Verified & Trusted</h3>
          <p className="text-gray-600 text-sm">
            Background checks and certification verification build customer trust.
          </p>
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-8 mb-12">
        <div className="flex items-center justify-center mb-4">
          <_Star className="w-5 h-5 text-yellow-500 fill-current" />
          <_Star className="w-5 h-5 text-yellow-500 fill-current mx-1" />
          <_Star className="w-5 h-5 text-yellow-500 fill-current" />
          <_Star className="w-5 h-5 text-yellow-500 fill-current mx-1" />
          <_Star className="w-5 h-5 text-yellow-500 fill-current" />
          <span className="ml-2 text-gray-700 font-semibold">4.9/5</span>
        </div>
        <p className="text-center text-gray-700 italic mb-2">
          &quot;Finally, a platform that actually supports guides instead of extracting every dollar.
          Setup took about 20 minutes. First booking in 6 days. I&apos;ve saved $8,000 in intermediary fees in just 3 months.&quot;
        </p>
        <p className="text-center text-sm text-gray-600">
          — Sarah Chen, AMGA Certified Guide, Yosemite
        </p>
      </div>

      {/* By The Numbers - Value Quantification */}
      <div className="bg-white rounded-lg border-2 border-emerald-200 p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
          What This Means for Your Business
        </h2>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="text-center p-4">
            <div className="text-4xl font-bold text-emerald-600 mb-2">$25-35K</div>
            <p className="text-gray-700 font-medium mb-1">Saved Annually</p>
            <p className="text-sm text-gray-600">Eliminate 30% intermediary cuts on $100K bookings</p>
          </div>
          <div className="text-center p-4">
            <div className="text-4xl font-bold text-emerald-600 mb-2">200+ Hours</div>
            <p className="text-gray-700 font-medium mb-1">Saved Per Year</p>
            <p className="text-sm text-gray-600">Worth 15-20 more booking days ($8-11K)</p>
          </div>
          <div className="text-center p-4">
            <div className="text-4xl font-bold text-emerald-600 mb-2">$50-100K</div>
            <p className="text-gray-700 font-medium mb-1">Infrastructure Value</p>
            <p className="text-sm text-gray-600">Professional payment processing, booking system, customer discovery</p>
          </div>
          <div className="text-center p-4">
            <div className="text-4xl font-bold text-emerald-600 mb-2">$0</div>
            <p className="text-gray-700 font-medium mb-1">What You Pay</p>
            <p className="text-sm text-gray-600">No setup fees, no monthly fees, no commissions</p>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-lg font-semibold text-gray-900">
            Your total annual benefit: <span className="text-emerald-600">$40-75K</span>
          </p>
        </div>
      </div>

      {/* First-Mover Advantage */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8 mb-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Get Positioned Early
          </h2>
          <p className="text-gray-700 mb-4">
            Smart guides are joining now, before competition increases in their market. Early members benefit from:
          </p>
          <ul className="space-y-3 text-gray-700 mb-6">
            <li className="flex items-start">
              <Check className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Better search positioning:</strong> Longer history and more reviews mean you appear first in customer searches</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Elite status while it&apos;s achievable:</strong> Build Platform Champion or Elite Performer status before your market gets crowded</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Founding member recognition:</strong> Permanent &quot;Founding Guide&quot; badge and enhanced referral economics</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Less competition:</strong> Don&apos;t wait until every guide in your area already has an established profile</span>
            </li>
          </ul>
          <p className="text-sm text-gray-600 italic">
            By the time most guides discover this platform in 6-12 months, you&apos;ll already have established reviews, strong positioning, and Elite status.
          </p>
        </div>
      </div>

      {/* Example Guide Profiles */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Meet Our Top-Earning Guides
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Your profile will look like this—professional, trustworthy, and ready to book
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Guide Card 1 - Jake Morrison */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
            <div className="relative h-48 bg-gradient-to-br from-slate-700 to-slate-900">
              <div className="absolute top-3 left-3">
                <span className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded">
                  Summer
                </span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <_Mountain className="w-20 h-20 text-white opacity-20" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Rocky Mountains, CO</h3>
                  <p className="text-sm text-gray-600">Rocky Mountain Summit Trek</p>
                </div>
                <div className="flex items-center">
                  <_Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="ml-1 text-sm font-semibold">4.9</span>
                  <span className="ml-1 text-xs text-gray-500">(248)</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">3 days</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">$899</span>
                <span className="text-sm text-gray-600">per person</span>
              </div>
            </div>
          </div>

          {/* Guide Card 2 */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
            <div className="relative h-48 bg-gradient-to-br from-emerald-700 to-emerald-900">
              <div className="absolute top-3 left-3">
                <span className="inline-block px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded">
                  Spring/Summer
                </span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <_Mountain className="w-20 h-20 text-white opacity-20" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Eldorado Canyon</h3>
                  <p className="text-sm text-gray-600">Technical Rock Climbing</p>
                </div>
                <div className="flex items-center">
                  <_Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="ml-1 text-sm font-semibold">4.9</span>
                  <span className="ml-1 text-xs text-gray-500">(112)</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">1 day</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">$349</span>
                <span className="text-sm text-gray-600">per person</span>
              </div>
            </div>
          </div>

          {/* Guide Card 3 - Featured */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow border-2 border-emerald-500">
            <div className="relative h-48 bg-gradient-to-br from-blue-700 to-blue-900">
              <div className="absolute top-3 left-3">
                <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
                  Expert Certified
                </span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <_Mountain className="w-20 h-20 text-white opacity-20" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Mount Rainier, WA</h3>
                  <p className="text-sm text-gray-600">Summit Mountaineering</p>
                </div>
                <div className="flex items-center">
                  <_Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="ml-1 text-sm font-semibold">4.95</span>
                  <span className="ml-1 text-xs text-gray-500">(203)</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">2 days</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">$450</span>
                <span className="text-sm text-gray-600">per person</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            <strong className="text-emerald-600">Featured Guide Badge</strong> shown when you complete profile + 5 verified bookings
          </p>
        </div>
      </div>

      {/* Switching Platform Acknowledgment */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
        <div className="flex items-start">
          <_AlertCircle className="w-6 h-6 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Switching platforms?</h4>
            <p className="text-gray-700 text-sm mb-3">
              We know you&apos;ve done this before and it&apos;s exhausting. That&apos;s why we:
            </p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Allow data import from CSV (services, availability, photos)</li>
              <li>• Provide human support throughout (call/text us anytime)</li>
              <li>• Never lock you in (export your data anytime)</li>
              <li>• Show transparent pricing upfront (no surprises)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Secondary CTA */}
      <div className="text-center mb-8">
        <button
          onClick={onGetStarted}
          className="inline-flex items-center px-8 py-4 bg-emerald-600 text-white text-lg font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
        >
          Create Your Guide Profile
          <ChevronRight className="ml-2 w-5 h-5" />
        </button>
        <p className="mt-4 text-sm text-gray-600">
          No credit card required • Free to join • Cancel anytime
        </p>
      </div>

      {/* Trust Signals */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600">
          <div className="flex items-center">
            <Shield className="w-4 h-4 mr-1.5 text-emerald-600" />
            <span>256-bit SSL Encryption</span>
          </div>
          <div className="flex items-center">
            <Check className="w-4 h-4 mr-1.5 text-emerald-600" />
            <span>TCPA Compliant</span>
          </div>
          <div className="flex items-center">
            <Heart className="w-4 h-4 mr-1.5 text-emerald-600" />
            <span>Built by outdoor guides</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreSignupValue;
